export {Privacy as Page};

import {Helmet} from "react-helmet-async";
import messages from "../../messages/messages.js";

const ext = {target: "_blank", rel: "noopener noreferrer"};

function Privacy() {
    return (
        <section className="text-white">

            <Helmet>
                <title>{messages["SITE.NAME"]} | Privacy Policy</title>
                <meta name="description" content={`The ${messages["SITE.NAME"]} Privacy Policy`}/>
            </Helmet>

            <div>
                <h1>{messages["SITE.NAME"]} Privacy Policy</h1>
                <p><strong>Last Updated:</strong> October 11, 2026</p>
                <p>{messages["SITE.NAME"]}, accessible from https://www.{messages["DOMAIN"]}/, is operated by Unbroken
                    Technology LLC d/b/a The Nightcore Network ("we," "us," "our"). One of our main priorities is the
                    privacy of our visitors. This Privacy Policy describes the information we collect, how we use it,
                    who we share it with, and the choices you have.</p>
                <p>If you have questions about this Privacy Policy, contact us at <a
                    href="mailto:support@nightcore.com">support@nightcore.com</a>.</p>
                <p>This Privacy Policy applies to our website and the services we provide through it. It does not apply
                    to information collected offline or through channels other than this website.</p>

                <h2>Consent</h2>
                <p>By using our website, you consent to this Privacy Policy and agree to its terms.</p>

                <h2>Information we collect</h2>
                <ul>
                    <li><strong>Account information.</strong> When you register for an account, we ask only for your
                        email address. Sign-in is handled by Google Firebase Authentication. We do not collect your
                        name, phone number, postal address, or other personal information beyond your email.</li>
                    <li><strong>Linked accounts (optional).</strong> If you choose to link your Discord or YouTube
                        account, we store the identifiers needed to connect it to your account, as described below.</li>
                    <li><strong>Payments.</strong> Subscriptions and other purchases are processed by Stripe or PayPal.
                        We do not receive or store your full card or bank account details.</li>
                    <li><strong>Usage and diagnostic data.</strong> We collect log data (see "Log Files"), aggregated
                        usage statistics through Google Analytics, and error monitoring, browser tracing, and session
                        replay data through Sentry. We configure Sentry to remove or redact sensitive and personal data
                        before transmission.</li>
                    <li><strong>YouTube data.</strong> We use YouTube API Services, as described in the next
                        section.</li>
                </ul>

                <h2>YouTube API Services</h2>
                <p>{messages["SITE.NAME"]} uses YouTube API Services to discover, display, and play music from YouTube
                    creators. By using {messages["SITE.NAME"]}, you agree to be bound by the <a {...ext}
                    href="https://www.youtube.com/t/terms">YouTube Terms of Service</a>. Google's handling of
                    information is described in the <a {...ext} href="https://www.google.com/policies/privacy">Google
                    Privacy Policy</a>.</p>
                <h3>What YouTube data we access and store</h3>
                <ul>
                    <li><strong>Public video and channel data.</strong> For songs and creators listed on our site, we
                        use the YouTube Data API to retrieve publicly available information such as video titles,
                        descriptions, thumbnails, durations, publish dates, and public statistics (for example view and
                        subscriber counts), and channel names and profile images. This data is not about you as a
                        visitor; it describes public YouTube content.</li>
                    <li><strong>Your linked YouTube channel (optional).</strong> If you click "Link YouTube account,"
                        you are asked by Google to grant read-only access to your YouTube account. We use that access
                        one time, to read your channel ID and channel name so we can connect your channel to your
                        Network membership. We store only your channel ID and channel name. We do not store your Google
                        access token or refresh token, and we do not access your private videos, subscriptions, watch
                        history, or any other account data. We cannot upload, edit, or delete anything on your
                        channel.</li>
                </ul>
                <h3>How we use and share YouTube data</h3>
                <p>We use YouTube data only to operate {messages["SITE.NAME"]}: to list songs, show creator and
                    channel profiles, display statistics, and play music through the embedded YouTube player. Public
                    YouTube data is displayed publicly on our website. We do not sell YouTube data, and we do not share
                    it with third parties other than the service providers that host and operate our website on our
                    behalf.</p>
                <p>YouTube data we store is cached on our servers and refreshed on a regular schedule by automated
                    jobs so that what we display stays current.</p>
                <h3>Cookies and the YouTube player</h3>
                <p>Music on our site plays through the embedded YouTube player. YouTube and Google may store, access,
                    or collect information directly or indirectly on or from your device, including by placing,
                    accessing, or recognizing cookies or similar technologies, as described in the Google Privacy
                    Policy.</p>
                <h3>Revoking access and deleting your data</h3>
                <p>You can revoke {messages["SITE.NAME"]}'s access to your YouTube account at any time through the <a
                    {...ext} href="https://security.google.com/settings/security/permissions">Google security settings
                    page</a>. Because we do not keep your access token, we have no ongoing access to your account
                    after linking.</p>
                <p>To remove the YouTube channel link (channel ID and channel name) stored on your account, or to
                    delete your account entirely, email <a href="mailto:support@nightcore.com">support@nightcore.com</a>.
                    We will delete that data within 7 days of your request.</p>

                <h2>How we use your information</h2>
                <p>We use the information we collect to:</p>
                <ul>
                    <li>Provide, operate, and maintain our website</li>
                    <li>Improve, personalize, and expand our website</li>
                    <li>Understand and analyze how you use our website</li>
                    <li>Develop new products, services, features, and functionality</li>
                    <li>Communicate with you, including for customer service and to send you updates and other
                        information relating to the website
                    </li>
                    <li>Process subscriptions and payments</li>
                    <li>Find and prevent fraud and abuse</li>
                </ul>

                <h2>Log Files</h2>
                <p>{messages["SITE.NAME"]} follows a standard procedure of using log files. The information collected
                    by log files includes internet protocol (IP) addresses, browser type, Internet Service Provider
                    (ISP), date and time stamp, and referring/exit pages. We use it to administer the site, analyze
                    trends, and protect the site against abuse.</p>

                <h2>Cookies and Similar Technologies</h2>
                <p>{messages["SITE.NAME"]} uses cookies and browser storage to keep you signed in, remember your
                    preferences, and measure how the site is used. Third-party services we use may also set cookies,
                    including Google Analytics, Google Firebase, the embedded YouTube player, Stripe, and PayPal. We do
                    not display third-party advertising and do not use advertising cookies.</p>
                <p>You can choose to disable cookies through your browser settings. Some features, such as signing in
                    or playing music, may not work without them.</p>

                <h2>Third-Party Services</h2>
                <p>We rely on the following third parties, each of which handles information under its own
                    privacy policy:</p>
                <ul>
                    <li>Google (YouTube API Services, YouTube embedded player, Firebase Authentication, Google
                        Analytics): <a {...ext} href="https://www.google.com/policies/privacy">Google Privacy
                        Policy</a></li>
                    <li>Discord (optional account linking): <a {...ext}
                        href="https://discord.com/privacy">Discord Privacy Policy</a></li>
                    <li>Stripe and PayPal (payments): <a {...ext} href="https://stripe.com/privacy">Stripe Privacy
                        Policy</a>, <a {...ext} href="https://www.paypal.com/myaccount/privacy/privacyhub">PayPal
                        Privacy Statement</a></li>
                    <li>Sentry (error monitoring): <a {...ext} href="https://sentry.io/privacy/">Sentry Privacy
                        Policy</a></li>
                </ul>

                <h2>CCPA Privacy Rights (Do Not Sell My Personal Information)</h2>
                <p>Under the CCPA, among other rights, California consumers have the right to:</p>
                <p>Request that a business that collects a consumer's personal data disclose the categories and specific
                    pieces of personal data that a business has collected about consumers.</p>
                <p>Request that a business delete any personal data about the consumer that a business has
                    collected.</p>
                <p>Request that a business that sells a consumer's personal data, not sell the consumer's personal
                    data. We do not sell personal data.</p>
                <p>If you make a request, we have one month to respond to you. If you would like to exercise any of
                    these rights, please contact us.</p>

                <h2>GDPR Data Protection Rights</h2>
                <p>Every user is entitled to the following:</p>
                <p>The right to access – You have the right to request copies of your personal data.</p>
                <p>The right to rectification – You have the right to request that we correct your personal data if it
                    is inaccurate.</p>
                <p>The right to erasure – You have the right to request that we erase your personal data, under certain
                    conditions.</p>
                <p>The right to restrict processing – You have the right to request that we restrict the processing of
                    your personal data, under certain conditions.</p>
                <p>The right to object to processing – You have the right to object to our processing of your personal
                    data, under certain conditions.</p>
                <p>The right to data portability – You have the right to request that we transfer the data that we have
                    collected to another organization, or directly to you, under certain conditions.</p>
                <p>If you make a request, we have one month to respond to you. If you would like to exercise any of
                    these rights, please contact us.</p>

                <h2>Children's Information</h2>
                <p>{messages["SITE.NAME"]} does not knowingly collect any personally identifiable information from
                    children under the age of 13. If you think that your child provided this kind of information on our
                    website, please contact us immediately and we will promptly remove such information from our
                    records.</p>

                <h2>Changes to This Privacy Policy</h2>
                <p>We may update this Privacy Policy from time to time. When we do, we will update the "Last Updated"
                    date at the top of this page.</p>

                <h2>Contact Us</h2>
                <p>Unbroken Technology LLC d/b/a The Nightcore Network<br/>
                    <a href="mailto:support@nightcore.com">support@nightcore.com</a></p>
            </div>
        </section>
    );
}
